import { useState } from "react";
import { BRAND } from "../constants/brand";
import PageHero from "../components/PageHero";

const API_URL = process.env.REACT_APP_CHAT_API_URL || "";

const BENEFITS = [
  "401(k) with company match", "Company vehicle", "Dental insurance",
  "Flexible schedule", "Health insurance", "Life insurance",
  "Paid time off & holidays", "Paid training", "Vision insurance",
  "Work from home eligible", "Professional development", "Remote work options",
];

const POSITIONS = [
  {
    title: "Laboratory Sales Representative",
    department: "Sales",
    tags: ["Full-time", "Remote", "Commission + Base"],
    location: "Remote",
    requiredDocs: "resume and cover letter",
    summary: "We are seeking a dynamic and driven Laboratory Sales Representative to promote and sell our portfolio of laboratory services. The successful candidate will manage and grow existing accounts, prospect new business, and provide technical consultation to clients.",
    responsibilities: [
      "Develop and execute a territory sales plan to meet or exceed sales targets",
      "Build and maintain strong relationships with lab managers, technicians, and procurement teams",
      "Conduct product demonstrations and technical presentations",
      "Understand client needs and recommend appropriate laboratory solutions",
      "Provide post-sale support and ensure customer satisfaction",
      "Stay informed on industry trends and competitor products",
    ],
    qualifications: [
      "Experience selling to physician offices, hospitals, court systems, and businesses",
      "Familiarity with regulatory requirements (CLIA, CAP, etc.)",
      "Existing customer relationships in the territory",
    ],
  },
  {
    title: "Certified Professional Coding Specialist",
    department: "Revenue Cycle Management",
    tags: ["Full-time", "On-site", "Non-Exempt"],
    location: "Miramar Beach, FL or Milledgeville, GA",
    requiredDocs: "resume and cover letter",
    summary: "The Certified Professional Coding Specialist is responsible for the accurate and timely submission of claims and the coding and billing of clinical laboratory services for Labarium Diagnostic Systems, Inc. This individual evaluates medical records and charge entries to ensure completeness, accuracy, and compliance with ICD-10-CM, HCPCS, and CPT code sets.",
    responsibilities: [
      "Apply professional judgment and coding expertise to evaluate and correct code assignment issues, ensuring accurate and compliant claim submission",
      "Evaluate medical record documentation and coding to optimize reimbursement by ensuring laboratory codes and documentation accurately reflect CPT, ICD-10-CM, and HCPCS policies",
      "Interpret medical information — including diagnoses, symptoms, and procedures — to assign and sequence correct ICD-10-CM and CPT codes",
      "Review documentation to identify missing or inaccurate billing codes and communicate education needs to clinical or billing staff",
      "Perform monthly random billing compliance audits to proactively identify potential billing discrepancies and compliance risks",
      "Make recommendations for updates to policies and procedures; collaborate with the RCM team to revise and maintain the chargemaster file",
      "Review state and federal Medicare reimbursement guidelines to ensure completeness and accuracy of claims prior to submission",
      "Evaluate records and prepare management reports on coding-related denial trends and billing discrepancies for leadership review",
      "Provide technical guidance in identifying and resolving coding issues including incomplete diagnoses, ambiguous documentation, and non-compliant codes",
    ],
    qualifications: [
      "High school diploma or equivalent required; Associate's degree in Healthcare Administration or related field preferred",
      "Active Certified Professional Coder (CPC) certification issued by AAPC required",
      "Minimum 2 years of experience in professional medical record coding services, preferably in a clinical laboratory or outpatient setting",
      "Thorough knowledge of ICD-10-CM and CPT coding guidelines, medical terminology, and Medicare reimbursement regulations",
      "Experience with clinical laboratory-specific billing, including Medicare LCDs and NCDs (preferred)",
      "Prior experience with pharmacogenomics (PGx) or molecular diagnostic coding (preferred)",
    ],
  },
  {
    title: "Clinical Laboratory Revenue Cycle Specialist",
    department: "Revenue Cycle Management",
    tags: ["Full-time", "On-site", "Non-Exempt"],
    location: "Miramar Beach, FL or Milledgeville, GA",
    requiredDocs: "resume and cover letter",
    summary: "The Clinical Laboratory Revenue Cycle Specialist supports the billing and reimbursement operations for Labarium Diagnostic Systems' clinical laboratory services. This role is responsible for accurate claim preparation, coding review, claim submission, and follow-up on payer responses to ensure timely and accurate reimbursement for all laboratory testing services across Labarium's network.",
    responsibilities: [
      "Prepare and submit laboratory claims to Medicare, Medicaid, and commercial insurance carriers",
      "Review laboratory test orders and supporting documentation to ensure accurate CPT and ICD-10 coding prior to submission",
      "Resolve claim edits and rejections prior to submission to reduce denial rates and accelerate reimbursement",
      "Conduct denial management and payer follow-up to secure timely and accurate reimbursement",
      "Prepare and submit appeals and required supporting documentation for denied or underpaid claims",
      "Maintain accurate and complete patient billing records in compliance with HIPAA and payer requirements",
      "Work closely with laboratory accessioning and operations teams to identify and correct billing discrepancies",
      "Monitor claim status and aging accounts receivable reports to ensure timely collections",
      "Ensure compliance with payer policies, Medicare/Medicaid billing regulations, and LCD/NCD requirements",
      "Support internal audits, compliance reviews, and payer audit responses as needed",
    ],
    qualifications: [
      "Minimum 2 years of experience in healthcare billing or laboratory revenue cycle operations",
      "Working knowledge of CPT coding and ICD-10-CM diagnosis coding for clinical laboratory services",
      "Experience with Medicare, Medicaid, and commercial payer claim submission and follow-up",
      "Strong analytical abilities for identifying underpayment, overpayment trends, and denial patterns",
      "Certified Professional Coder (CPC), Certified Coding Specialist (CCS), or equivalent certification (preferred)",
      "Experience with pharmacogenomics (PGx) or molecular diagnostic laboratory billing (preferred)",
      "Prior experience in a multi-site or roll-up laboratory billing environment (preferred)",
    ],
  },
  {
    title: "Accountant / Senior Accountant",
    department: "Finance",
    tags: ["Full-time", "Hybrid", "Exempt"],
    location: "Miramar Beach, FL / Milledgeville, GA",
    reportsTo: "VP, Finance",
    travel: "Up to 10%",
    requiredDocs: "resume and cover letter",
    summary: "Labarium Diagnostic Systems is seeking a detail-oriented and analytically sharp Accountant or Senior Accountant to join the Finance Division, reporting directly to the Vice President of Finance. This role is integral to Labarium's financial operations during a period of active growth — including multi-site laboratory acquisitions, multi-state regulatory compliance, and the development of scalable finance infrastructure. The successful candidate will own core accounting functions from general ledger management and month-end close to revenue cycle reconciliation and acquisition accounting. Level placement (Accountant vs. Senior Accountant) will be determined based on experience and demonstrated proficiency in healthcare or multi-entity accounting environments.",
    responsibilities: [
      {
        group: "General Ledger & Month-End Close",
        items: [
          "Maintain the general ledger across all Labarium entities and subsidiaries, ensuring accuracy, completeness, and timely recording of all financial transactions",
          "Lead or support month-end, quarter-end, and year-end close processes, including journal entry preparation, accruals, prepaid amortization, and account reconciliations",
          "Reconcile balance sheet accounts monthly; research and resolve discrepancies in a timely manner with appropriate documentation",
          "Prepare and post standard and adjusting journal entries in accordance with GAAP; maintain supporting workpapers for all entries",
          "Coordinate with the VP of Finance to ensure close timelines are met and financial statements are delivered accurately and on schedule",
        ],
      },
      {
        group: "Revenue Cycle & Healthcare Billing Accounting",
        items: [
          "Reconcile laboratory revenue to billing system reports, remittance advice, and bank deposits; investigate and resolve variances",
          "Record and analyze payer reimbursements, contractual adjustments, denials, and bad debt reserves across Medicare, Medicaid, and commercial payers",
          "Support accounting for pharmacogenomics and specialty test revenue, including Proove Genomics panels, in coordination with the billing and revenue cycle teams",
          "Assist in the development and maintenance of healthcare-specific revenue recognition policies consistent with ASC 606 and applicable payer contract terms",
          "Prepare accounts receivable aging analyses and support cash collections reporting",
        ],
      },
      {
        group: "Accounts Payable & Cash Management",
        items: [
          "Oversee or support the accounts payable function, including invoice coding, approval workflow, vendor payments, and 1099 compliance",
          "Perform daily and monthly bank reconciliations across all operating accounts; monitor cash positions and flag anomalies to the VP of Finance",
          "Manage petty cash, corporate card reconciliations, and employee expense reimbursements in accordance with company policy",
          "Assist in cash flow forecasting and working capital analysis as directed",
        ],
      },
      {
        group: "Acquisition & Multi-Entity Accounting",
        items: [
          "Support accounting for laboratory acquisitions, including purchase price allocation, opening balance sheet establishment, and integration of acquired entities into Labarium's consolidated financial reporting",
          "Perform pro forma corporate consolidation and reporting; create consolidated projected financials including detailed income statements and balance sheets",
          "Construct debt amortization tables, earnout obligations, and other deferred consideration arising from acquisition structures",
          "Maintain intercompany accounts and perform intercompany eliminations as part of consolidated financial reporting",
          "Assist in due diligence financial analysis for prospective acquisitions, including normalization adjustments and historical financial review",
        ],
      },
      {
        group: "Financial Reporting & Compliance",
        items: [
          "Prepare internal financial statements and management reports on a monthly and quarterly basis, including income statements, balance sheets, and cash flow summaries by entity",
          "Support preparation of annual financial statements and coordination with external auditors or review engagements",
          "Assist in the preparation of federal and multi-state income tax workpapers in coordination with external tax advisors; maintain fixed asset schedules and depreciation records",
          "Ensure compliance with applicable financial reporting obligations under Delaware corporate law and Florida state regulations; support multi-state payroll tax and sales/use tax filings",
          "Maintain organized, audit-ready financial records and supporting documentation across all entities",
        ],
      },
      {
        group: "Systems, Processes & Special Projects",
        items: [
          "Support implementation and optimization of accounting software and ERP systems as Labarium scales its finance infrastructure",
          "Document accounting policies, procedures, and internal controls; identify and recommend process improvement opportunities",
          "Assist the VP of Finance with ad hoc financial analysis, board reporting, lender compliance, and special projects as needed",
          "Collaborate cross-functionally with Operations, Legal & Compliance, HR, and Revenue Cycle to support financially integrated decision-making",
        ],
      },
    ],
    qualifications: [
      {
        group: "Accountant — Required",
        items: [
          "Bachelor's degree in Accounting, Finance, or a closely related field",
          "Minimum of 2–4 years of accounting experience, with demonstrated proficiency in general ledger, reconciliations, and month-end close",
          "Working knowledge of GAAP and financial statement preparation",
          "Proficiency in accounting software (QuickBooks, Sage, NetSuite, or equivalent); advanced Excel skills including pivot tables, VLOOKUP/XLOOKUP, and financial modeling",
          "Strong organizational skills and attention to detail; ability to manage multiple priorities in a deadline-driven environment",
          "Effective written and verbal communication skills; ability to collaborate with non-finance stakeholders",
        ],
      },
      {
        group: "Senior Accountant — Required (in addition to Accountant requirements)",
        items: [
          "Minimum of 4–7 years of progressive accounting experience, with demonstrated independent ownership of close processes and financial reporting",
          "Experience in a multi-entity or consolidated accounting environment; exposure to healthcare, clinical laboratory, or revenue cycle accounting strongly preferred",
          "CPA license or active CPA candidacy preferred at this level",
          "Demonstrated ability to apply technical accounting judgment on complex transactions, including revenue recognition, lease accounting, and business combinations",
          "Prior experience supporting external audit, tax preparation coordination, or lender reporting",
        ],
      },
      {
        group: "Preferred (All Levels)",
        items: [
          "CPA license or active candidacy",
          "Experience with clinical laboratory or healthcare services accounting, including Medicare/Medicaid reimbursement and contractual adjustment accounting",
          "Familiarity with acquisition accounting, seller note tracking, and post-M&A financial integration",
          "Experience with multi-state payroll tax, sales/use tax, or state corporate income tax filings",
          "Exposure to ERP implementation or finance system migration projects",
          "Comfort operating in a high-growth, entrepreneurial environment where processes are being built rather than inherited",
        ],
      },
    ],
    competencies: [
      "Financial Accuracy & Integrity", "Analytical Problem-Solving",
      "Healthcare Revenue Cycle Acumen", "Multi-Entity Consolidation",
      "Process Discipline & Documentation", "Cross-Functional Collaboration",
      "Deadline Management", "Entrepreneurial Adaptability",
    ],
  },
  {
    title: "In-House Litigator",
    department: "Legal & Compliance",
    tags: ["Full-time", "Hybrid", "Exempt"],
    location: "Miramar Beach, FL / Milledgeville, GA",
    reportsTo: "VP, Legal & Compliance",
    travel: "Up to 25%",
    requiredDocs: "resume, cover letter, and Georgia Bar certificate of good standing",
    licensure: "Active Georgia Bar (Required — Non-Negotiable)",
    summary: "Labarium Diagnostic Systems is seeking an experienced, Georgia-licensed In-House Litigator to serve as the company's lead litigation counsel. This is a high-impact, mission-critical role within Labarium's growing Legal & Compliance Division, reporting directly to the Vice President of Legal & Compliance. The In-House Litigator will own the company's litigation portfolio across all jurisdictions, manage external counsel relationships, and provide strategic risk counsel to executive leadership. The ideal candidate brings deep experience in healthcare regulatory litigation, commercial disputes, and post-M&A legal integration, and is energized by building a litigation function from the ground up within a dynamic, entrepreneurial organization.",
    responsibilities: [
      {
        group: "Litigation Management",
        items: [
          "Serve as primary litigation counsel for all pending and future disputes involving Labarium and its subsidiaries, including commercial contract disputes, employment matters, regulatory enforcement actions, and reimbursement-related claims",
          "Manage all phases of litigation, including pre-suit investigation, pleadings, discovery, motion practice, trial preparation, and resolution, with particular fluency in Georgia state and federal courts",
          "Evaluate, retain, supervise, and control costs of outside counsel across all matters; maintain litigation hold protocols and matter management systems",
          "Develop and maintain a comprehensive litigation calendar; provide regular status reports and strategic updates to the VP of Legal & Compliance and, where appropriate, the executive team",
          "Conduct post-acquisition legal integration reviews to identify and remediate inherited litigation exposure, pending claims, and unresolved pre-closing obligations",
        ],
      },
      {
        group: "Healthcare Regulatory & Compliance Litigation",
        items: [
          "Advise on litigation risk arising from CLIA, HIPAA, the Anti-Kickback Statute, the Stark Law, the False Claims Act, and applicable state laboratory licensing regulations",
          "Respond to government investigations, civil investigative demands (CIDs), and subpoenas; coordinate with compliance leadership on voluntary disclosure strategies",
          "Support defense of billing and coding disputes, Medicare/Medicaid audit findings, and payer contract enforcement actions",
          "Provide litigation-informed guidance to the compliance team on pharmacogenomics reimbursement risk, including CPT/ICD-10 coding challenges and payer policy disputes",
        ],
      },
      {
        group: "Transactional & M&A Litigation Support",
        items: [
          "Review acquisition transaction documents — including purchase agreements, seller notes, operating agreements, and personal guaranties — to identify representations, warranties, and indemnification obligations relevant to litigation exposure",
          "Advise leadership on dispute resolution mechanisms in acquisition agreements, including earn-out disputes, indemnity claims, and post-closing adjustments",
          "Draft and negotiate dispute resolution provisions in future acquisition and commercial agreements; provide litigation risk assessments during due diligence",
        ],
      },
      {
        group: "Employment & HR Litigation",
        items: [
          "Manage employment-related claims including wage and hour disputes, wrongful termination, discrimination, and non-compete enforcement across Labarium's multi-state workforce",
          "Advise HR leadership on disciplinary matters, separations, and workforce actions with a view toward litigation risk minimization",
          "Draft and review employment agreements, separation agreements, and restrictive covenants in coordination with outside counsel as needed",
        ],
      },
      {
        group: "Legal Operations & Strategy",
        items: [
          "Establish and maintain litigation management infrastructure, including matter tracking, e-billing, document preservation, and reporting dashboards",
          "Develop standard dispute resolution playbooks and escalation protocols for operational leaders",
          "Monitor legislative and regulatory developments affecting clinical laboratory litigation risk in Georgia and other states of operation; brief executive leadership as warranted",
          "Collaborate cross-functionally with Finance, Operations, Compliance, and HR to embed proactive legal risk management into business processes",
        ],
      },
    ],
    qualifications: [
      {
        group: "Required",
        items: [
          "Juris Doctor (J.D.) from an ABA-accredited law school",
          "Active license to practice law in the State of Georgia — this requirement is non-negotiable",
          "Minimum of 2 years of litigation experience, with at least 2 years involving M&A and contracts, as well as some experience in healthcare, clinical laboratory, or life sciences matters",
          "Demonstrated experience managing litigation in Georgia state courts and the U.S. District Courts within the Eleventh Circuit",
          "Substantial familiarity with HIPAA, CLIA, the False Claims Act, and federal and state healthcare fraud and abuse statutes",
          "Proven ability to manage outside counsel engagements with a focus on strategic alignment and cost efficiency",
          "Strong command of litigation risk assessment, e-discovery, and legal hold procedures",
          "Excellent written and oral advocacy skills; ability to distill complex legal matters for non-attorney executives",
          "Willingness to travel to laboratory locations and legal proceedings, including in Georgia (Milledgeville and surrounding area), as required",
        ],
      },
      {
        group: "Preferred",
        items: [
          "Admission or eligibility for admission to the Florida Bar (multi-state practice is highly valued)",
          "Experience with pharmacogenomics, molecular diagnostics, or specialty laboratory reimbursement disputes",
          "Background in post-acquisition integration legal work, including seller note enforcement, indemnification claims, and operating agreement interpretation",
          "Familiarity with Medicare and Medicaid audit response, CID response, and voluntary disclosure protocol",
          "Experience in a high-growth, multi-site healthcare services environment",
          "Prior in-house experience; candidates transitioning from healthcare-focused litigation practices are also strongly encouraged to apply",
        ],
      },
    ],
    competencies: [
      "Strategic Legal Judgment", "Litigation Risk Management",
      "Healthcare Regulatory Acumen", "Cross-Functional Collaboration",
      "Executive Communication", "Transactional Literacy",
      "Organizational Integrity & Ethics", "Entrepreneurial Adaptability",
    ],
  },
];

export default function CareersPage({ setPage }) {
  const [form, setForm] = useState({ firstName: "", lastName: "", email: "", phone: "", position: "", message: "" });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleChange = e => setForm(prev => ({ ...prev, [e.target.name]: e.target.value }));

  const handleApply = (title) => {
    setForm(prev => ({ ...prev, position: title }));
    setTimeout(() => {
      document.getElementById("apply-form")?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 100);
  };

  const handleSubmit = async e => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const res = await fetch(`${API_URL}/api/contact`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...form, subject: `Career Application: ${form.position}`, type: "careers" }),
      });
      if (!res.ok) throw new Error("Failed");
      setSubmitted(true);
    } catch {
      setError("There was an issue submitting your application. Please email us directly at careers@labarium.com.");
    }
    setLoading(false);
  };

  const inputStyle = { width: "100%", padding: "12px 14px", border: `1.5px solid ${BRAND.lightGray}`, borderRadius: 8, fontFamily: "'Barlow', sans-serif", fontSize: 14, color: BRAND.textDark, outline: "none", background: BRAND.offWhite, boxSizing: "border-box" };
  const labelStyle = { display: "block", fontFamily: "'Barlow', sans-serif", fontSize: 11, fontWeight: 700, letterSpacing: "0.15em", color: BRAND.navy, marginBottom: 6 };

  return (
    <>
      <PageHero eyebrow="JOIN OUR TEAM" title="Start Your Journey" highlight="with Labarium" subtitle="We're building the next-generation clinical laboratory company. Join a mission-driven team reshaping healthcare diagnostics across the United States." />

      <div className="rx-pad" style={{ background: BRAND.offWhite, padding: "64px 32px 96px" }}>
        <div style={{ maxWidth: 1280, margin: "0 auto" }}>

          {/* Open positions header */}
          <div style={{ marginBottom: 40 }}>
            <div style={{ fontFamily: "'Barlow', sans-serif", fontSize: 11, fontWeight: 700, letterSpacing: "0.22em", color: BRAND.gold, marginBottom: 8 }}>OPEN POSITIONS</div>
            <h2 style={{ fontFamily: "'Playfair Display', serif", fontWeight: 700, fontSize: "clamp(22px,3vw,34px)", color: BRAND.navy, margin: 0 }}>
              {POSITIONS.length} Roles Available
            </h2>
          </div>

          {/* Job listings */}
          <div style={{ display: "flex", flexDirection: "column", gap: 28, marginBottom: 60 }}>
            {POSITIONS.map((pos, i) => (
              <div key={i} style={{ background: BRAND.white, borderRadius: 16, border: `1px solid ${BRAND.lightGray}`, overflow: "hidden", boxShadow: "0 4px 20px rgba(0,53,122,0.06)" }}>
                {/* Card header */}
                <div style={{ background: BRAND.navy, padding: "24px 32px", position: "relative", display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 16 }}>
                  <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: 3, background: BRAND.gold }} />
                  <div style={{ flex: 1 }}>
                    <div style={{ fontFamily: "'Barlow', sans-serif", fontSize: 11, fontWeight: 700, letterSpacing: "0.16em", color: "rgba(255,255,255,0.5)", marginBottom: 6, textTransform: "uppercase" }}>
                      {pos.department}
                    </div>
                    <h3 style={{ fontFamily: "'Playfair Display', serif", fontWeight: 700, fontSize: 22, color: BRAND.white, margin: "0 0 10px" }}>{pos.title}</h3>
                    <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: (pos.reportsTo || pos.travel || pos.licensure) ? 12 : 0 }}>
                      {pos.tags.map(tag => (
                        <span key={tag} style={{ background: "rgba(192,168,140,0.2)", color: BRAND.gold, fontFamily: "'Barlow', sans-serif", fontSize: 11, fontWeight: 700, padding: "4px 12px", borderRadius: 12 }}>{tag}</span>
                      ))}
                    </div>
                    {(pos.reportsTo || pos.travel || pos.licensure) && (
                      <div style={{ display: "flex", gap: 20, flexWrap: "wrap" }}>
                        {pos.reportsTo && (
                          <div style={{ fontFamily: "'Barlow', sans-serif", fontSize: 11, color: "rgba(255,255,255,0.5)" }}>
                            <span style={{ color: "rgba(255,255,255,0.3)" }}>Reports To: </span>{pos.reportsTo}
                          </div>
                        )}
                        {pos.travel && (
                          <div style={{ fontFamily: "'Barlow', sans-serif", fontSize: 11, color: "rgba(255,255,255,0.5)" }}>
                            <span style={{ color: "rgba(255,255,255,0.3)" }}>Travel: </span>{pos.travel}
                          </div>
                        )}
                        {pos.licensure && (
                          <div style={{ fontFamily: "'Barlow', sans-serif", fontSize: 11, color: BRAND.gold, fontWeight: 700 }}>
                            ⚖ {pos.licensure}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                  <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 8 }}>
                    <div style={{ fontFamily: "'Barlow', sans-serif", fontSize: 12, color: "rgba(255,255,255,0.5)" }}>📍 {pos.location}</div>
                    <button
                      onClick={() => handleApply(pos.title)}
                      style={{
                        background: "linear-gradient(135deg, #c9a96e 0%, #dfc08a 50%, #c9a96e 100%)",
                        color: BRAND.navyDeep, border: "none",
                        fontFamily: "'Barlow', sans-serif", fontWeight: 700, fontSize: 12,
                        letterSpacing: "0.1em", padding: "10px 24px", borderRadius: 8, cursor: "pointer",
                        textTransform: "uppercase",
                      }}
                    >
                      Apply Now →
                    </button>
                  </div>
                </div>

                {/* Licensure alert */}
                {pos.licensure && (
                  <div style={{ background: "rgba(201,169,110,0.08)", borderBottom: `1px solid rgba(201,169,110,0.2)`, padding: "12px 32px", display: "flex", alignItems: "center", gap: 10 }}>
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none"><circle cx="12" cy="12" r="10" stroke={BRAND.gold} strokeWidth="1.8"/><path d="M12 8v4M12 16h.01" stroke={BRAND.gold} strokeWidth="2" strokeLinecap="round"/></svg>
                    <span style={{ fontFamily: "'Barlow', sans-serif", fontSize: 12, fontWeight: 700, color: BRAND.gold }}>Licensure Requirement:</span>
                    <span style={{ fontFamily: "'Barlow', sans-serif", fontSize: 12, color: BRAND.textMid }}>{pos.licensure} — Candidates without an active Georgia Bar license will not be considered.</span>
                  </div>
                )}

                {/* Card body */}
                <div style={{ padding: "28px 32px" }}>
                  <div style={{ fontFamily: "'Barlow', sans-serif", fontSize: 11, fontWeight: 700, letterSpacing: "0.18em", color: BRAND.navy, marginBottom: 10 }}>POSITION SUMMARY</div>
                  <p style={{ fontFamily: "'Barlow', sans-serif", fontSize: 14, color: BRAND.textMid, lineHeight: 1.8, marginBottom: 28 }}>{pos.summary}</p>

                  {/* Responsibilities & Qualifications */}
                  {(() => {
                    const isGrouped = pos.responsibilities.length > 0 && typeof pos.responsibilities[0] === "object";
                    const isQualsGrouped = pos.qualifications.length > 0 && typeof pos.qualifications[0] === "object";
                    const bullet = (item, idx) => (
                      <div key={idx} style={{ display: "flex", gap: 10, alignItems: "flex-start" }}>
                        <div style={{ width: 5, height: 5, borderRadius: "50%", background: BRAND.gold, flexShrink: 0, marginTop: 6 }} />
                        <span style={{ fontFamily: "'Barlow', sans-serif", fontSize: 13, color: BRAND.textMid, lineHeight: 1.65 }}>{item}</span>
                      </div>
                    );
                    return (
                      <div className="two-col" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 32, marginBottom: (pos.competencies || pos.applyNote) ? 28 : 0 }}>
                        <div>
                          <div style={{ fontFamily: "'Barlow', sans-serif", fontSize: 11, fontWeight: 700, letterSpacing: "0.18em", color: BRAND.navy, marginBottom: 14 }}>KEY RESPONSIBILITIES</div>
                          {isGrouped ? (
                            <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
                              {pos.responsibilities.map((grp, gi) => (
                                <div key={gi}>
                                  <div style={{ fontFamily: "'Barlow', sans-serif", fontSize: 10, fontWeight: 700, letterSpacing: "0.14em", color: BRAND.gold, textTransform: "uppercase", marginBottom: 8, paddingBottom: 4, borderBottom: `1px solid rgba(201,169,110,0.2)` }}>{grp.group}</div>
                                  <div style={{ display: "flex", flexDirection: "column", gap: 7 }}>{grp.items.map(bullet)}</div>
                                </div>
                              ))}
                            </div>
                          ) : (
                            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>{pos.responsibilities.map(bullet)}</div>
                          )}
                        </div>
                        <div>
                          <div style={{ fontFamily: "'Barlow', sans-serif", fontSize: 11, fontWeight: 700, letterSpacing: "0.18em", color: BRAND.navy, marginBottom: 14 }}>QUALIFICATIONS</div>
                          {isQualsGrouped ? (
                            <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
                              {pos.qualifications.map((grp, gi) => (
                                <div key={gi}>
                                  <div style={{ fontFamily: "'Barlow', sans-serif", fontSize: 10, fontWeight: 700, letterSpacing: "0.14em", color: BRAND.gold, textTransform: "uppercase", marginBottom: 8, paddingBottom: 4, borderBottom: `1px solid rgba(201,169,110,0.2)` }}>{grp.group}</div>
                                  <div style={{ display: "flex", flexDirection: "column", gap: 7 }}>{grp.items.map(bullet)}</div>
                                </div>
                              ))}
                            </div>
                          ) : (
                            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>{pos.qualifications.map(bullet)}</div>
                          )}
                        </div>
                      </div>
                    );
                  })()}

                  {/* Core Competencies */}
                  {pos.competencies && (
                    <div style={{ marginTop: 4 }}>
                      <div style={{ height: 1, background: BRAND.lightGray, marginBottom: 20 }} />
                      <div style={{ fontFamily: "'Barlow', sans-serif", fontSize: 11, fontWeight: 700, letterSpacing: "0.18em", color: BRAND.navy, marginBottom: 14 }}>CORE COMPETENCIES</div>
                      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(180px, 1fr))", gap: 8 }}>
                        {pos.competencies.map(c => (
                          <div key={c} style={{ background: BRAND.offWhite, border: `1px solid ${BRAND.lightGray}`, borderRadius: 8, padding: "8px 12px", display: "flex", alignItems: "center", gap: 7 }}>
                            <div style={{ width: 5, height: 5, borderRadius: "50%", background: BRAND.gold, flexShrink: 0 }} />
                            <span style={{ fontFamily: "'Barlow', sans-serif", fontSize: 11, color: BRAND.navy, fontWeight: 600, lineHeight: 1.4 }}>{c}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Special apply note */}
                  {pos.applyNote && (
                    <div style={{ marginTop: 20, background: "rgba(0,53,122,0.04)", border: `1px dashed ${BRAND.lightGray}`, borderRadius: 8, padding: "12px 16px" }}>
                      <span style={{ fontFamily: "'Barlow', sans-serif", fontSize: 12, fontWeight: 700, color: BRAND.navy }}>How to Apply: </span>
                      <span style={{ fontFamily: "'Barlow', sans-serif", fontSize: 12, color: BRAND.textMid }}>{pos.applyNote}</span>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* Benefits */}
          <div style={{ background: BRAND.white, borderRadius: 16, border: `1px solid ${BRAND.lightGray}`, padding: "28px 32px", boxShadow: "0 4px 20px rgba(0,53,122,0.06)", marginBottom: 60 }}>
            <div style={{ fontFamily: "'Barlow', sans-serif", fontSize: 11, fontWeight: 700, letterSpacing: "0.18em", color: BRAND.gold, marginBottom: 16 }}>BENEFITS PACKAGE</div>
            <div className="stat-grid" style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 10 }}>
              {BENEFITS.map(b => (
                <div key={b} style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <div style={{ width: 6, height: 6, borderRadius: "50%", background: BRAND.navy, flexShrink: 0 }} />
                  <span style={{ fontFamily: "'Barlow', sans-serif", fontSize: 12, color: BRAND.textMid }}>{b}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Application form */}
          <div id="apply-form">
            <div style={{ fontFamily: "'Barlow', sans-serif", fontSize: 11, fontWeight: 700, letterSpacing: "0.22em", color: BRAND.gold, marginBottom: 16 }}>APPLY NOW</div>
            <div style={{ background: BRAND.white, borderRadius: 16, border: `1px solid ${BRAND.lightGray}`, overflow: "hidden", boxShadow: "0 4px 20px rgba(0,53,122,0.06)", maxWidth: 760 }}>
              <div style={{ background: BRAND.navy, padding: "24px 32px", position: "relative" }}>
                <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: 3, background: BRAND.gold }} />
                <h2 style={{ fontFamily: "'Playfair Display', serif", fontWeight: 700, fontSize: 26, color: BRAND.white, margin: "0 0 4px" }}>Submit Your Application</h2>
                <p style={{ fontFamily: "'Barlow', sans-serif", fontSize: 13, color: "rgba(255,255,255,0.6)", margin: 0 }}>Join the team building the future of laboratory diagnostics</p>
              </div>
              <div style={{ padding: "32px" }}>
                {submitted ? (
                  <div style={{ textAlign: "center", padding: "48px 20px" }}>
                    <div style={{ width: 64, height: 64, borderRadius: "50%", background: "rgba(0,53,122,0.08)", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 20px" }}>
                      <svg width="28" height="28" viewBox="0 0 24 24" fill="none"><path d="M20 6L9 17l-5-5" stroke={BRAND.navy} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
                    </div>
                    <h3 style={{ fontFamily: "'Playfair Display', serif", fontWeight: 700, fontSize: 24, color: BRAND.navy, marginBottom: 12 }}>Application Submitted!</h3>
                    <p style={{ fontFamily: "'Barlow', sans-serif", fontSize: 14, color: BRAND.textMid, lineHeight: 1.7, marginBottom: 24 }}>Thank you for your interest in joining Labarium. Our team will review your application and reach out within 5–7 business days.</p>
                    <button onClick={() => { setSubmitted(false); setForm({ firstName: "", lastName: "", email: "", phone: "", position: "", message: "" }); }} style={{ background: BRAND.navy, color: BRAND.white, border: "none", fontFamily: "'Barlow', sans-serif", fontWeight: 700, fontSize: 12, letterSpacing: "0.1em", padding: "12px 24px", borderRadius: 8, cursor: "pointer" }}>SUBMIT ANOTHER →</button>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit}>
                    <div className="form-grid" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 16 }}>
                      {[["firstName", "First Name"], ["lastName", "Last Name"]].map(([name, label]) => (
                        <div key={name}>
                          <label style={labelStyle}>{label.toUpperCase()} *</label>
                          <input name={name} required value={form[name]} onChange={handleChange} style={inputStyle}
                            onFocus={e => e.target.style.borderColor = BRAND.navy}
                            onBlur={e => e.target.style.borderColor = BRAND.lightGray} />
                        </div>
                      ))}
                    </div>
                    {[["email", "Email Address", "email"], ["phone", "Phone Number", "tel"]].map(([name, label, type]) => (
                      <div key={name} style={{ marginBottom: 16 }}>
                        <label style={labelStyle}>{label.toUpperCase()} *</label>
                        <input type={type} name={name} required={name === "email"} value={form[name]} onChange={handleChange} style={inputStyle}
                          onFocus={e => e.target.style.borderColor = BRAND.navy}
                          onBlur={e => e.target.style.borderColor = BRAND.lightGray} />
                      </div>
                    ))}
                    <div style={{ marginBottom: 16 }}>
                      <label style={labelStyle}>POSITION APPLYING FOR *</label>
                      <select
                        name="position"
                        required
                        value={form.position}
                        onChange={handleChange}
                        style={{ ...inputStyle, appearance: "none", backgroundImage: "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='8' viewBox='0 0 12 8'%3E%3Cpath d='M1 1l5 5 5-5' stroke='%23666' strokeWidth='1.5' fill='none' strokeLinecap='round'/%3E%3C/svg%3E\")", backgroundRepeat: "no-repeat", backgroundPosition: "right 14px center", paddingRight: 36 }}
                        onFocus={e => e.target.style.borderColor = BRAND.navy}
                        onBlur={e => e.target.style.borderColor = BRAND.lightGray}
                      >
                        <option value="">— Select a position —</option>
                        {POSITIONS.map(p => (
                          <option key={p.title} value={p.title}>{p.title}</option>
                        ))}
                      </select>
                    </div>
                    <div style={{ marginBottom: 16 }}>
                      <label style={labelStyle}>COVER MESSAGE</label>
                      <textarea name="message" rows={5} value={form.message} onChange={handleChange} placeholder="Tell us about your experience and why you'd like to join Labarium..." style={{ ...inputStyle, resize: "vertical" }}
                        onFocus={e => e.target.style.borderColor = BRAND.navy}
                        onBlur={e => e.target.style.borderColor = BRAND.lightGray} />
                    </div>
                    {(() => {
                      const docs = POSITIONS.find(p => p.title === form.position)?.requiredDocs;
                      return (
                        <div style={{ background: BRAND.offWhite, borderRadius: 10, padding: "12px 16px", border: `1px dashed ${BRAND.lightGray}`, marginBottom: 20, textAlign: "center" }}>
                          <div style={{ fontFamily: "'Barlow', sans-serif", fontSize: 13, color: BRAND.textMid }}>
                            📎 {docs ? <>Your <strong style={{ color: BRAND.navy }}>{docs}</strong> will be requested during follow-up</> : "Resume and supporting documents will be requested during follow-up"}
                          </div>
                        </div>
                      );
                    })()}
                    <button type="submit" disabled={loading} style={{ width: "100%", background: loading ? BRAND.textMid : BRAND.navy, color: BRAND.white, border: "none", fontFamily: "'Barlow', sans-serif", fontWeight: 700, fontSize: 13, letterSpacing: "0.12em", padding: "16px 24px", borderRadius: 10, cursor: loading ? "not-allowed" : "pointer" }}>
                      {loading ? "SUBMITTING…" : "SUBMIT APPLICATION →"}
                    </button>
                    {error && <div style={{ background: "#fef2f2", border: "1px solid #fecaca", borderRadius: 8, padding: "12px 16px", fontFamily: "'Barlow', sans-serif", fontSize: 13, color: "#b91c1c", marginTop: 14 }}>{error}</div>}
                  </form>
                )}
              </div>
            </div>
          </div>

        </div>
      </div>
    </>
  );
}
